import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMockQuery } from "@/hooks/useMockQuery";
import { apiClient } from "@/lib/apiClient";
import { WhooLiveMap } from "@/components/map/WhooLiveMap";
import { MapFilters } from "@/components/map/MapFilters";
import { STATE_ORDER } from "@/lib/eventState";

// Compact, interactive homepage preview — same map, markers and filters as /map.
export function LiveAroundMap({ events: eventsProp }) {
  const navigate = useNavigate();
  const { data: fetched } = useMockQuery(
    () => (eventsProp ? Promise.resolve(eventsProp) : apiClient.getMapEvents()),
    [eventsProp]
  );
  const events = eventsProp || fetched;

  const [activeStates, setActiveStates] = useState(STATE_ORDER);
  const [activeCategories, setActiveCategories] = useState([]);

  const categories = useMemo(() => {
    const m = new Map();
    (events || []).forEach((e) => {
      if (!m.has(e.category)) m.set(e.category, e.categoryColor);
    });
    return [...m.entries()].map(([key, color]) => ({ key, label: key, color }));
  }, [events]);

  const filtered = useMemo(() => {
    if (!events) return [];
    return events.filter(
      (e) =>
        activeStates.includes(e.state) &&
        (activeCategories.length === 0 || activeCategories.includes(e.category))
    );
  }, [events, activeStates, activeCategories]);

  const toggleState = (s) =>
    setActiveStates((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  const toggleCategory = (c) =>
    setActiveCategories((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  return (
    <div
      data-testid="live-around-map"
      className="flex h-full min-h-[340px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
    >
      <div className="border-b border-border p-3">
        <MapFilters
          className="max-h-20 overflow-y-auto"
          activeStates={activeStates}
          onToggleState={toggleState}
          categories={categories}
          activeCategories={activeCategories}
          onToggleCategory={toggleCategory}
        />
      </div>
      <div className="relative flex-1">
        <WhooLiveMap
          events={filtered}
          height="100%"
          compact
          onOpenFull={() => navigate("/map")}
        />
      </div>
    </div>
  );
}

export default LiveAroundMap;
